import { useEffect, useRef, useState , type ReactNode } from 'react';
import {
  Box, Button, IconButton, Popover, TextField, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import {
  DateRange as DateRangeIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import {
  addDays, differenceInDays, format as formatDate,
} from 'date-fns';
import { getPresetRanges, type DateRangeValue } from '../utils/date-presets';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  sx?: any;
  footer?: ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalise(value: DateRangeValue): DateRangeValue {
  if (!value || !value.filter(Boolean).length) return [null, null];
  return [value[0] ? new Date(value[0]) : null, value[1] ? new Date(value[1]) : null];
}

function formatInput(value: DateRangeValue): string {
  if (!value[0] && !value[1]) return '';
  const from = value[0] ? formatDate(value[0], 'dd/MM/yy') : '';
  const to = value[1] ? formatDate(value[1], 'dd/MM/yy') : '';
  return `${from} - ${to}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DateRangePicker({ value, onChange, sx, footer }: DateRangePickerProps) {
  const [internalValue, setInternalValue] = useState<DateRangeValue>(normalise(value));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setInternalValue(normalise(value));
  }, [value]);

  function handleSave() {
    if (!internalValue[0] || !internalValue[1]) return;
    onChange(internalValue);
    setPopoverOpen(false);
  }

  function handleChange(newValue: DateRangeValue) {
    setInternalValue(newValue);
  }

  function handleClose() {
    setPopoverOpen(false);
    setInternalValue(normalise(value));
  }

  function moveRange(e: React.MouseEvent, direction: 1 | -1) {
    e.stopPropagation();
    if (!value[0] || !value[1]) return;
    const numDays = differenceInDays(value[1], value[0]) + 1;
    const newRange: DateRangeValue = [
      addDays(value[0], numDays * direction),
      addDays(value[1], numDays * direction),
    ];
    onChange(newRange);
  }

  function isRangeMatching(dateRange: DateRangeValue) {
    const a = internalValue;
    const firstMatch = (!dateRange[0] && !a[0]) || (dateRange[0] && a[0] && dateRange[0].getTime() === a[0].getTime());
    const secondMatch = (!dateRange[1] && !a[1]) || (dateRange[1] && a[1] && dateRange[1].getTime() === a[1].getTime());
    return firstMatch && secondMatch;
  }

  return (
    <>
      <TextField
        ref={anchorRef}
        size="small"
        focused={popoverOpen}
        value={formatInput(internalValue)}
        onClick={() => setPopoverOpen(true)}
        sx={{ width: 220, cursor: 'pointer', ...sx }}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: <DateRangeIcon sx={{ mr: 0.5, color: 'action.active', fontSize: 20 }} />,
            sx: { pr: 0.5, cursor: 'pointer' },
            endAdornment: (
              <Box sx={{ display: 'flex', gap: 0 }}>
                <Tooltip title="Previous period">
                  <IconButton size="small" sx={{ p: 0 }} onClick={(e) => moveRange(e, -1)}>
                    <KeyboardArrowLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Next period">
                  <IconButton size="small" sx={{ p: 0 }} onClick={(e) => moveRange(e, 1)}>
                    <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
          },
        }}
      />
      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleClose}
        disableRestoreFocus
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <StaticDateRangePicker
              displayStaticWrapperAs={isMobile ? 'mobile' : 'desktop'}
              value={internalValue}
              onChange={handleChange}
              onAccept={() => isMobile && handleSave()}
              disableAutoMonthSwitching
              // Hide the built-in Cancel/OK action bar — we use our own Apply button.
              slotProps={{ actionBar: { actions: [] } }}
            />
            {!isMobile && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {getPresetRanges().map((pr) => (
                  <Button
                    key={pr.key}
                    size="small"
                    variant={isRangeMatching(pr.dateRange) ? 'contained' : 'outlined'}
                    onClick={() => setInternalValue(pr.dateRange)}
                  >
                    {pr.title}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{
            display: 'flex', alignItems: 'center', mt: 1, gap: 1,
            justifyContent: footer ? 'space-between' : 'flex-end',
          }}>
            {footer}
            <Button
              variant="contained"
              color="primary"
              fullWidth={isMobile}
              onClick={handleSave}
              disabled={!internalValue[0] || !internalValue[1]}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
